import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/Login.css';

// List of inappropriate words to filter (lowercase)
const BLOCKED_WORDS = [
  'fuck', 'shit', 'ass', 'bitch', 'damn', 'cunt', 'dick', 'cock', 'pussy',
  'asshole', 'bastard', 'slut', 'whore', 'fag', 'faggot', 'nigger', 'nigga',
  'retard', 'retarded', 'rape', 'rapist', 'piss', 'porn', 'sex', 'penis',
  'vagina', 'anus', 'dildo', 'orgasm', 'masturbat', 'jizz', 'cum', 'sperm',
  'nazi', 'hitler', 'kkk', 'terrorist', 'kill', 'murder', 'suicide'
];

// Check if text contains inappropriate words
const containsProfanity = (text) => {
  if (!text) return false;
  const lowerText = text.toLowerCase().replace(/[^a-z]/g, '');
  return BLOCKED_WORDS.some(word => lowerText.includes(word));
};

// Password strength calculation
function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: '' };

  let score = 0;
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password),
  };

  // Base score for meeting minimum length
  if (password.length >= 6) score += 1;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;

  // Bonus for character variety
  if (checks.lowercase && checks.uppercase) score += 1;
  if (checks.number) score += 1;
  if (checks.special) score += 1;

  // Cap at 5 for our 5-segment display
  score = Math.min(score, 5);

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
  const colors = ['', 'var(--error)', 'var(--warning)', 'var(--primary-light)', 'var(--primary)', 'var(--primary-dark)'];

  return {
    score,
    label: labels[score],
    color: colors[score],
    checks,
  };
}

// Password strength indicator component
function PasswordStrengthIndicator({ password }) {
  const strength = getPasswordStrength(password);

  if (!password) return null;

  return (
    <div className="password-strength" aria-live="polite">
      <div className="password-strength-bar">
        {[1, 2, 3, 4, 5].map((segment) => (
          <div
            key={segment}
            className={`strength-segment ${segment <= strength.score ? 'active' : ''}`}
            style={{ backgroundColor: segment <= strength.score ? strength.color : undefined }}
          />
        ))}
      </div>
      <span className="password-strength-label" style={{ color: strength.color }}>
        {strength.label}
      </span>
    </div>
  );
}

// Progress bar component for new user signup flow - segmented style
function ProgressBar({ currentStep, totalSteps }) {
  const segments = [];
  for (let i = 1; i <= totalSteps; i++) {
    segments.push(
      <div
        key={i}
        className={`progress-segment ${i <= currentStep ? 'active' : ''}`}
      />
    );
  }

  return (
    <div className="progress-bar-wrapper">
      <div className="progress-bar-container">
        {segments}
      </div>
      <span className="progress-label">{currentStep}/{totalSteps}</span>
    </div>
  );
}

// Step 1: Email entry
function EmailStep({ email, setEmail, onContinue, error, setError, isLoading }) {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    onContinue();
  };

  return (
    <form className="login-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          autoFocus
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      <button type="submit" className="btn-primary" disabled={isLoading}>
        {isLoading ? 'Checking...' : 'Continue'}
      </button>
    </form>
  );
}

// Step 2a: Password for existing users
function PasswordStep({
  email,
  displayName,
  password,
  setPassword,
  onBack,
  onSubmit,
  error,
  isLoading
}) {
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password) {
      return;
    }
    onSubmit();
  };

  const handleForgotPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setResetError('');
    } catch (err) {
      setResetError('Failed to send reset email. Please try again.');
    }
  };

  return (
    <div className="step-container">
      <div className="welcome-message">
        <h2>Welcome back!</h2>
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <div className="email-display">
            <span>{email}</span>
            <button type="button" className="btn-change" onClick={onBack}>
              Change
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            autoFocus
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        {resetSent && (
          <div className="success-message">
            Password reset email sent! Check your inbox.
          </div>
        )}

        {resetError && <div className="error-message">{resetError}</div>}

        <button type="submit" className="btn-primary" disabled={isLoading}>
          {isLoading ? 'Signing in...' : 'Sign In'}
        </button>

        <button
          type="button"
          className="btn-link forgot-password"
          onClick={handleForgotPassword}
        >
          Forgot password?
        </button>
      </form>
    </div>
  );
}

// Step 2b: Create password for new users
function CreatePasswordStep({
  email,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  onBack,
  onContinue,
  error,
  setError
}) {
  const handleSubmit = (e) => {
    e.preventDefault();

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    onContinue();
  };

  return (
    <div className="step-container">
      <ProgressBar currentStep={2} totalSteps={4} />

      <div className="step-header">
        <h2>Let's set up your account</h2>
        <p className="step-subtext">
          Already have an account?{' '}
          <button type="button" className="btn-link-inline" onClick={onBack}>
            Try a different email
          </button>
        </p>
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a password"
            required
            autoFocus
            aria-describedby="password-strength-hint"
          />
          <PasswordStrengthIndicator password={password} />
          <p id="password-strength-hint" className="password-hint">
            Use 8+ characters with uppercase, numbers, and symbols for a stronger password
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            required
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="button-group">
          <button type="button" className="btn-secondary" onClick={onBack}>
            Back
          </button>
          <button type="submit" className="btn-primary">
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Step 3: Username for new users
function UsernameStep({
  username,
  setUsername,
  onBack,
  onContinue,
  error,
  setError
}) {
  const [usernameStatus, setUsernameStatus] = useState({
    checking: false,
    available: null,
    message: ''
  });

  const checkUsername = useCallback(async (value) => {
    if (!value || value.length < 3) {
      setUsernameStatus({ checking: false, available: null, message: '' });
      return;
    }

    setUsernameStatus({ checking: true, available: null, message: 'Checking...' });

    try {
      const response = await api.checkUsernameAvailability(value);
      setUsernameStatus({
        checking: false,
        available: response.data.available,
        message: response.data.available ? 'Username available' : response.data.reason,
      });
    } catch (err) {
      setUsernameStatus({ checking: false, available: null, message: '' });
    }
  }, []);

  useEffect(() => {
    if (username) {
      const timer = setTimeout(() => {
        checkUsername(username);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [username, checkUsername]);

  const handleUsernameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!username || username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }

    if (containsProfanity(username)) {
      setError('Username contains inappropriate language');
      return;
    }

    if (usernameStatus.available === false) {
      setError('Please choose an available username');
      return;
    }

    if (usernameStatus.checking) {
      return; // Wait for check to complete
    }

    onContinue();
  };

  return (
    <div className="step-container">
      <ProgressBar currentStep={3} totalSteps={4} />

      <div className="step-header">
        <h2>Choose a username</h2>
        <p className="step-subtext">
          This is how others will find you
        </p>
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="username">Username</label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={handleUsernameChange}
            placeholder="Choose a unique username"
            required
            autoFocus
            className={
              usernameStatus.available === false
                ? 'input-error'
                : usernameStatus.available === true
                  ? 'input-success'
                  : ''
            }
          />
          {usernameStatus.message && (
            <div className={`username-status ${
              usernameStatus.available === true
                ? 'status-success'
                : usernameStatus.available === false
                  ? 'status-error'
                  : ''
            }`}>
              {usernameStatus.message}
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="button-group">
          <button type="button" className="btn-secondary" onClick={onBack}>
            Back
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={usernameStatus.checking}
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Step 4: Display name for new users
function DisplayNameStep({
  displayName,
  setDisplayName,
  agreedToTerms,
  setAgreedToTerms,
  onBack,
  onSubmit,
  error,
  setError,
  isLoading
}) {
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!displayName || displayName.trim().length === 0) {
      return;
    }

    if (containsProfanity(displayName)) {
      setError('Display name contains inappropriate language');
      return;
    }

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    onSubmit();
  };

  return (
    <div className="step-container">
      <ProgressBar currentStep={4} totalSteps={4} />

      <div className="step-header">
        <h2>Choose a display name</h2>
        <p className="step-subtext">
          This is how you'll appear to others
        </p>
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="displayName">Display Name</label>
          <input
            type="text"
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
            required
            autoFocus
          />
        </div>

        <div className="form-group-checkbox">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <span className="checkbox-text">
              I agree to the{' '}
              <a href="/legal/terms" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </span>
          </label>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="button-group">
          <button type="button" className="btn-secondary" onClick={onBack}>
            Back
          </button>
          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? 'Creating account...' : 'Create Account'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Main Login component
function Login() {
  const [step, setStep] = useState('email'); // email, password, createPassword, username, displayName
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [existingUserDisplayName, setExistingUserDisplayName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Referral code handling
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);

  const { login, signup, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Check for referral code in URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Validate the referral code
      api.validateReferralCode(refCode)
        .then(response => {
          if (response.success && response.data.valid) {
            setReferralInfo({
              referrerUsername: response.data.referrerUsername,
              referrerDisplayName: response.data.referrerDisplayName,
            });
          }
        })
        .catch(err => {
          console.error('Invalid referral code:', err);
        });
    }
  }, [searchParams]);

  const handleEmailContinue = async () => {
    setError('');
    setIsLoading(true);

    try {
      const response = await api.checkEmailExists(email);

      if (response.data.exists) {
        setExistingUserDisplayName(response.data.displayName);
        setStep('password');
      } else {
        setStep('createPassword');
      }
    } catch (err) {
      setError('Unable to check email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    setError('');
    setIsLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/lists');
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };

  const handleSignup = async () => {
    setError('');
    setIsLoading(true);

    const result = await signup(email, password, username, displayName);

    if (result.success) {
      // Apply referral code if one was provided
      if (referralCode) {
        try {
          await api.applyReferralCode(referralCode);
          // Refresh user data to get the updated referralTrialExpiresAt field
          // This ensures the SubscriptionContext sees the active trial
          await refreshUser();
          console.log('Referral code applied successfully');
        } catch (err) {
          // Don't block signup if referral code fails
          console.error('Failed to apply referral code:', err);
        }
      }
      navigate('/lists');
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };

  const goBack = (toStep) => {
    setError('');
    setStep(toStep);
  };

  const renderStep = () => {
    switch (step) {
      case 'email':
        return (
          <EmailStep
            email={email}
            setEmail={setEmail}
            onContinue={handleEmailContinue}
            error={error}
            setError={setError}
            isLoading={isLoading}
          />
        );

      case 'password':
        return (
          <PasswordStep
            email={email}
            displayName={existingUserDisplayName}
            password={password}
            setPassword={setPassword}
            onBack={() => goBack('email')}
            onSubmit={handleLogin}
            error={error}
            isLoading={isLoading}
          />
        );

      case 'createPassword':
        return (
          <CreatePasswordStep
            email={email}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            onBack={() => goBack('email')}
            onContinue={() => {
              setError('');
              setStep('username');
            }}
            error={error}
            setError={setError}
          />
        );

      case 'username':
        return (
          <UsernameStep
            username={username}
            setUsername={setUsername}
            onBack={() => goBack('createPassword')}
            onContinue={() => {
              setError('');
              setStep('displayName');
            }}
            error={error}
            setError={setError}
          />
        );

      case 'displayName':
        return (
          <DisplayNameStep
            displayName={displayName}
            setDisplayName={setDisplayName}
            agreedToTerms={agreedToTerms}
            setAgreedToTerms={setAgreedToTerms}
            onBack={() => goBack('username')}
            onSubmit={handleSignup}
            error={error}
            setError={setError}
            isLoading={isLoading}
          />
        );

      default:
        return null;
    }
  };

  const isEmailStep = step === 'email';

  return (
    <div className="login-container">
      <div className="login-card">
        <div className={`login-header ${!isEmailStep ? 'compact' : ''}`}>
          <img src="/logo.png" alt="Vibary Logo" className="app-logo" />
          {isEmailStep && (
            <>
              <h1 className="app-title">Vibary</h1>
              <p className="app-subtitle">Your personal library of vibes</p>
            </>
          )}
        </div>

        {/* Referral banner */}
        {referralInfo && isEmailStep && (
          <div className="referral-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
            </svg>
            <span>
              {referralInfo.referrerDisplayName || referralInfo.referrerUsername} invited you!
              Sign up to get 2 weeks of Premium free.
            </span>
          </div>
        )}

        {renderStep()}
      </div>
    </div>
  );
}

export default Login;
